/**
 * FilesPickerModal — in-place overlay version of the Files page.
 *
 * Used by the X-ray viewer (and any other host page) when the user picks
 * "Cloud Storage" so the files browser appears stacked on top of the host
 * page's content (e.g. the X-ray viewer iframe) without covering the rest
 * of the PMS chrome (sidebar, top bar, etc.).
 *
 * Renders as an absolutely-positioned overlay — the host MUST wrap this
 * component (and the underlying content) in a `position: relative`
 * container so the overlay sizes to that container's bounds.
 */
import React from 'react'
import Files from '@pages/Files/Files.page'
import type { MockFileItem } from '@pages/Files/mockFilesData'
import './FilesPickerModal.css'

export interface FilesPickerModalProps {
  show: boolean
  onHide: () => void
  onFilesSelected: (files: MockFileItem[]) => void
  /** Folder name to auto-open on mount, e.g. "X-Ray Files". */
  initialOpenFolderName?: string
}

const FilesPickerModal: React.FC<FilesPickerModalProps> = ({
  show,
  onHide,
  onFilesSelected,
  initialOpenFolderName,
}) => {
  if (!show) return null

  const handleSelected = (files: MockFileItem[]): void => {
    onFilesSelected(files)
    onHide()
  }

  return (
    <div className="files-picker-overlay" role="dialog" aria-modal="true">
      <div
        className="files-picker-overlay-backdrop"
        onClick={onHide}
        aria-hidden
      />
      <div className="files-picker-overlay-panel">
        <Files
          selectionMode
          onFilesSelected={handleSelected}
          onSelectionCancel={onHide}
          initialOpenFolderName={initialOpenFolderName}
          convertImagesToDicom
        />
      </div>
    </div>
  )
}

export default FilesPickerModal
